import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

type BtnVariant = "primary" | "ghost" | "danger" | "success" | "tg";

export function Button({
  variant = "primary",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant }) {
  const base =
    "inline-flex items-center justify-center gap-1.5 h-8 px-3.5 text-xs font-semibold rounded-lg border whitespace-nowrap transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed";
  const v =
    variant === "primary"
      ? "bg-brand/15 text-brand border-brand/30 hover:bg-brand-grad hover:text-brand-fg hover:border-transparent hover:shadow-glow"
      : variant === "danger"
        ? "bg-red-900/40 text-red-300 border-red-700/50 hover:bg-red-500 hover:text-white hover:border-red-500"
        : variant === "success"
          ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/50 hover:bg-emerald-500 hover:text-white hover:border-emerald-500"
          : variant === "tg"
            ? "bg-sky-900/40 text-sky-300 border-sky-700/50 hover:bg-sky-500 hover:text-white hover:border-sky-500"
            : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-700 hover:text-white hover:border-zinc-600";
  return <button className={`${base} ${v} ${className}`} {...rest} />;
}

export function Input({
  className = "",
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`h-9 w-full px-3.5 rounded-lg bg-black/25 border border-white/[0.08] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition ${className}`}
      {...rest}
    />
  );
}

export function NumericInput({
  className = "",
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  return (
    <Input
      type="text"
      inputMode="decimal"
      pattern="[0-9]*"
      className={`${className} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      {...rest}
    />
  );
}

export function Card({
  title,
  children,
  className = "",
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-2xl card-elev p-4 md:p-5 ${className}`}>
      {title && (
        <div className="text-zinc-400 text-xs uppercase tracking-wider mb-3">{title}</div>
      )}
      {children}
    </div>
  );
}

export function PageHeader({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <header className="flex items-start sm:items-center justify-between mb-5 md:mb-6 pb-4 border-b border-zinc-800 gap-3 flex-wrap">
      <h1 className="text-xl md:text-2xl font-bold flex items-center gap-3 min-w-0 tracking-tight">
        <span className="w-1.5 h-5 md:h-7 bg-brand-grad rounded-full shrink-0 shadow-glow" />
        <span className="truncate">{title}</span>
      </h1>
      {right}
    </header>
  );
}

export function Pill({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "success" | "warning" | "danger" | "info" | "violet" | "cyan" | "pink";
  children: ReactNode;
}) {
  // Палитра расширена: раньше было 4 тона, и разные по смыслу чипы (транспорт
  // dd и double-hop, вид trial и paid) красились одинаково — на глаз не
  // отличались.
  const TONES: Record<string, string> = {
    success: "bg-emerald-900/50 text-emerald-300 border-emerald-700/50",
    warning: "bg-amber-900/40 text-amber-300 border-amber-700/50",
    danger: "bg-red-900/40 text-red-300 border-red-700/50",
    info: "bg-sky-900/40 text-sky-300 border-sky-700/50",
    violet: "bg-violet-900/40 text-violet-300 border-violet-700/50",
    cyan: "bg-cyan-900/40 text-cyan-300 border-cyan-700/50",
    pink: "bg-pink-900/40 text-pink-300 border-pink-700/50",
    neutral: "bg-zinc-800 text-zinc-300 border-zinc-700",
  };
  const c = TONES[tone] ?? TONES.neutral;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs border ${c}`}>
      {children}
    </span>
  );
}

/** Меню «⋯» в строке таблицы. Рендерится порталом в body: обёртка Table имеет
 *  overflow-x-auto (нужен для горизонтальной прокрутки) и обрезала бы выпадашку. */
export function RowActions({
  children,
  label = "⋯",
}: {
  children: (close: () => void) => ReactNode;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    // Скролл ВНУТРИ меню не должен его закрывать: слушатель стоит с capture,
    // поэтому ловил и события из самого дропдауна — список нельзя было
    // прокрутить, он схлопывался на первом же движении колеса.
    const onScrollResize = (e?: Event) => {
      if (e && menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="w-8 h-8 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors leading-none"
        aria-label="Действия"
      >
        {label}
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 60 }}
            onClick={(e) => e.stopPropagation()}
            className="w-max min-w-[210px] max-w-[300px] rounded-xl menu-surface p-1 origin-top-right animate-[popIn_.12s_ease-out]"
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/[0.07] overflow-x-auto card-elev">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = "left",
  fit = false,
}: {
  children: ReactNode;
  align?: "left" | "right";
  fit?: boolean;
}) {
  const a = align === "right" ? "text-right" : "text-left";
  return (
    <th
      className={`${a} text-xs uppercase tracking-wider text-zinc-400 font-medium px-4 py-2 bg-zinc-900/60 border-b border-zinc-800 ${
        fit ? "w-px whitespace-nowrap" : ""
      }`}
    >
      {children}
    </th>
  );
}

export function SortableTh({
  children,
  sortKey,
  currentSort,
  currentDir,
  onSort,
}: {
  children: ReactNode;
  sortKey: string;
  currentSort: string | null;
  currentDir: "asc" | "desc";
  onSort: (key: string) => void;
}) {
  const active = currentSort === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer select-none text-left text-xs uppercase tracking-wider font-medium px-4 py-2 bg-zinc-900/60 border-b border-zinc-800 hover:bg-zinc-800/60 transition-colors ${
        active ? "text-brand" : "text-zinc-400"
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        <SortArrow active={active} dir={currentDir} />
      </span>
    </th>
  );
}

function SortArrow({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) {
    return (
      <svg width="10" height="12" viewBox="0 0 10 12" className="opacity-30 shrink-0">
        <path d="M5 2 L8 5 L2 5 Z" fill="currentColor" />
        <path d="M5 10 L2 7 L8 7 Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" className="shrink-0">
      {dir === "desc" ? (
        <path d="M5 8 L1 3 L9 3 Z" fill="currentColor" />
      ) : (
        <path d="M5 2 L9 7 L1 7 Z" fill="currentColor" />
      )}
    </svg>
  );
}

export function Td({
  children,
  className = "",
  align = "left",
  fit = false,
}: {
  children: ReactNode;
  className?: string;
  align?: "left" | "right";
  fit?: boolean;
}) {
  const a = align === "right" ? "text-right" : "text-left";
  return (
    <td
      className={`px-4 py-3 border-b border-zinc-900 ${a} ${
        fit ? "w-px whitespace-nowrap" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={`inline-flex items-center gap-2.5 text-sm text-zinc-300 select-none ${
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <span className="relative inline-flex">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer appearance-none w-4 h-4 rounded border border-zinc-700 bg-zinc-900 checked:bg-brand checked:border-brand hover:border-zinc-500 checked:hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/30 transition-colors cursor-pointer disabled:cursor-not-allowed"
        />
        <svg
          className="pointer-events-none absolute inset-0 m-auto w-3 h-3 text-white opacity-0 peer-checked:opacity-100"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 6 L5 9 L10 3" />
        </svg>
      </span>
      {label}
    </label>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  maxWidth = "max-w-xl",
  elevated = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  // elevated — модалка, открытая ПОВЕРХ другой модалки (напр. карточка ноды из
  // карточки хоста). Без этого обе на z-50 и вложенная лезет ПОД родителя.
  elevated?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className={`fixed inset-0 ${elevated ? "z-[60]" : "z-50"} flex items-end sm:items-center justify-center p-0 sm:p-4`}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative w-full ${maxWidth} rounded-t-2xl sm:rounded-2xl card-elev shadow-pop max-h-[92vh] sm:max-h-[85vh] flex flex-col`}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/[0.06]">
          <h3 className="text-base sm:text-lg font-semibold text-zinc-50 truncate pr-4 flex items-center gap-2.5">
            <span className="w-1 h-4 rounded-full bg-brand-grad shrink-0" />
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.06] shrink-0 transition-colors"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        <div className="p-4 sm:p-6 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

export function TgLink({
  username,
  className = "",
  children,
}: {
  username: string | null | undefined;
  className?: string;
  children?: ReactNode;
}) {
  if (!username) return <span className="text-zinc-500">—</span>;
  return (
    <a
      href={`https://t.me/${username}`}
      target="_blank"
      rel="noreferrer"
      className={`text-zinc-100 hover:text-brand hover:underline ${className}`}
    >
      {children ?? `@${username}`}
    </a>
  );
}

// Тёмный стилизованный дропдаун (замена нативному <select> — тот рендерит
// белый нативный попап поверх тёмной темы). Поиск появляется при >8 опций.
//
// Опция принимает icon отдельным полем: раньше флаг клеили в строку label
// («🇷🇺 RU»), и эмодзи-флаг шире моноширинного символа — подписи разъезжались
// по вертикали. Иконка в своей колонке фиксированной ширины это чинит.
export function DarkSelect({
  value,
  onChange,
  options,
  placeholder = "— выбрать —",
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; hint?: string; icon?: string }[];
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);

  const sel = options.find((o) => o.value === value);
  const filtered = q
    ? options.filter((o) =>
        (o.label + " " + (o.hint ?? "")).toLowerCase().includes(q.toLowerCase()),
      )
    : options;

  // Список рисуем порталом: absolute-выпадашку обрезали карточки и модалки с
  // overflow (в «Выдать доступ» было видно только первую строку).
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const MENU_H = 288; // max-h-72
    const GAP = 4;
    const below = window.innerHeight - r.bottom - GAP;
    const above = r.top - GAP;
    // Открываем вверх только если снизу реально не помещается, а сверху места больше.
    const up = below < Math.min(MENU_H, 200) && above > below;
    // Ширина: не уже кнопки, но и не растягиваться по самой длинной опции до
    // края экрана — потолок в 1.5 ширины кнопки и остаток вьюпорта справа.
    const room = window.innerWidth - r.left - 12;
    const width = Math.max(160, Math.min(r.width * 1.5, room));
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    // Вверх — якорим НИЗ меню к верху кнопки (bottom): расчёт top по max-высоте
    // отрывал короткое меню от кнопки и вешал его «в центре экрана».
    if (up) setPos({ bottom: window.innerHeight - r.top + GAP, left, width });
    else setPos({ top: r.bottom + GAP, left, width });
  }, [open, q]);

  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, filtered.findIndex((o) => o.value === value)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return setOpen(false);
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => {
          const next = e.key === "ArrowDown" ? i + 1 : i - 1;
          return Math.max(0, Math.min(filtered.length - 1, next));
        });
      }
      if (e.key === "Enter") {
        const o = filtered[active];
        if (o) {
          onChange(o.value);
          setOpen(false);
          setQ("");
        }
      }
    };
    // Скролл ВНУТРИ меню не должен его закрывать: слушатель стоит с capture,
    // поэтому ловил и события из самого дропдауна — список нельзя было
    // прокрутить, он схлопывался на первом движении колеса.
    const onScrollResize = (e?: Event) => {
      if (e && menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open, filtered, active, onChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-9 pl-3 pr-2 rounded-lg bg-black/25 border text-sm text-left flex items-center gap-2 transition ${
          open ? "border-brand/60 ring-2 ring-brand/20" : "border-white/[0.08] hover:border-brand/40"
        } ${className}`}
      >
        {sel?.icon && (
          <span className="w-5 shrink-0 text-center leading-none">{sel.icon}</span>
        )}
        <span className={`flex-1 truncate ${sel ? "text-zinc-100" : "text-zinc-500"}`}>
          {sel?.label ?? placeholder}
        </span>
        <svg
          className={`text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: pos.width,
              zIndex: 70,
            }}
            className="rounded-xl menu-surface max-h-72 overflow-y-auto overflow-x-hidden p-1 animate-[popIn_.12s_ease-out]"
          >
            {options.length > 8 && (
              <input
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(0);
                }}
                placeholder="поиск…"
                className="w-full h-9 px-3 mb-1 bg-black/40 border-b border-white/[0.06] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none sticky top-0 z-10"
              />
            )}
            {filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  setQ("");
                }}
                className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  o.value === value
                    ? "text-brand bg-brand-soft/40"
                    : i === active
                      ? "bg-white/[0.07] text-zinc-200"
                      : "text-zinc-300"
                }`}
              >
                {o.icon && (
                  <span className="w-5 shrink-0 text-center leading-none">{o.icon}</span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate">{o.label}</span>
                  {o.hint && (
                    <span className="block text-[11px] text-zinc-500 truncate">{o.hint}</span>
                  )}
                </span>
                {o.value === value && (
                  <svg
                    className="shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-zinc-600">ничего не найдено</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

// Единый дропдаун проекта. Select — псевдоним DarkSelect: раньше рядом жили
// два почти одинаковых компонента, что и рождало разнобой в интерфейсе.
export const Select = DarkSelect;
